import React, { useEffect, useState } from "react"
import { Image, ImageStyle, Platform, StyleProp, StyleSheet, View, ViewStyle } from "react-native"
import Loader from "./loader"
import { log } from "./pwa"

const containerStyleFromImageStyle = (
  style: StyleProp<ImageStyle> | undefined
): StyleProp<ViewStyle> | undefined => {
  const flat = StyleSheet.flatten(style)
  if (!flat) return undefined
  const {
    resizeMode: _resizeMode,
    overlayColor: _overlayColor,
    tintColor: _tintColor,
    objectFit: _objectFit,
    ...layout
  } = flat
  return layout
}

type Props = {
  src?: string; // "https://example.com/image.jpg"
  alt: string; // "Example Image"
  style?: StyleProp<ImageStyle>;
  fallbackIcon: React.ReactNode; // shown when there is no src
  errorIcon: React.ReactNode; // shown when src fails to load
}

const DynamicImage:React.FC<Props> = ({src, alt, style, fallbackIcon, errorIcon}: Props) => {
  // web supports loading states, mobile does not
  const [loading, setLoading] = useState(Platform.OS == "web")
  const [error, setError] = useState(false)
  const hasSrc = typeof src === 'string' && src.trim().length > 0
  const placeholder = !hasSrc
    ? fallbackIcon
    : (error ? errorIcon : undefined)

  useEffect(() => {
    setError(false)
    setLoading(Platform.OS == "web" && hasSrc)
  }, [src, hasSrc])

  const containerStyle = containerStyleFromImageStyle(style)

  return (
    <>
      {
        placeholder ?
        <View style={[containerStyle, {justifyContent: "center", alignItems: "center"}]}>
          {placeholder}
        </View>
        : hasSrc ?
        <Image
          source={{uri: src}}
          style={[style, {display: loading ? "none" : "flex"}]}
          onLoad={() => {
            // re-renders could lead to a successful fetch which should
            // clear the error state
            setError(false)
          }}
          onError={(e) => {
            log.error(e, { tags: { message: 'error loading dynamic image', src } })
            setError(true)
          }}
          onLoadEnd={() => setLoading(false)}
          alt={alt}
        />
        : null
      }
      {loading && hasSrc && !placeholder && <Loader style={containerStyle} />}
    </>
  )
}

export default DynamicImage
